import type { ComponentType } from "react";
import { useState } from "react";
import { lazyWithRetry } from "@/utils/lazy-with-retry";

// React.lazy suspends on its first render even when its module has already loaded, and React 19 then holds the
// Suspense reveal until 300 ms after the last fallback. Once preload() has resolved, this renders the component
// directly, so it never suspends.
export function lazyWithPreload(
  load: () => Promise<{ default: ComponentType<object> }>,
) {
  let loaded: ComponentType<object> | undefined;
  let loading: Promise<{ default: ComponentType<object> }> | undefined;

  const preload = () =>
    (loading ??= load().then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error: unknown) => {
        // A later render or preload tries again.
        loading = undefined;
        throw error;
      },
    ));

  const Lazy = lazyWithRetry(preload);

  function LazyWithPreload(props: object) {
    // Keep one component type for this element's lifetime, so a module that loads after it mounted doesn't remount it.
    const [Component] = useState<ComponentType<object>>(() => loaded ?? Lazy);
    return <Component {...props} />;
  }

  return Object.assign(LazyWithPreload, { preload });
}
